import type { MouseEvent } from 'react';
import {
  Avatar,
  DateField,
  Pill,
  Select,
  TypeChip
} from '../primitives';
import type { CellValue, Column } from '../Table';
import { formatCell, pillStatusForCell } from './tableCellFormat';
import { TableCellEditor } from './TableCellEditor';

export interface TableCellDisplayProps {
  column: Column;
  value: CellValue;
  rowId: string;
  editTestId: string;
  isEditing: boolean;
  onStartEdit: () => void;
  onCommit: (newValue: CellValue) => void;
  onCancel: () => void;
}

function stopRowClick(e: MouseEvent) {
  e.stopPropagation();
}

export function TableCellDisplay({
  column,
  value,
  rowId,
  editTestId,
  isEditing,
  onStartEdit,
  onCommit,
  onCancel
}: TableCellDisplayProps) {
  const text = formatCell(value, column);
  const editable = column.editable !== false;
  const fieldId = `${rowId}-${column.key}`;

  if (!editable) {
    return <TableCellReadOnly column={column} value={value} text={text} />;
  }

  if (column.type === 'single_select' || column.type === 'person') {
    const baseOptions = column.options ?? [];
    // A `person` column carries its own empty-valued "Unassigned" option, so
    // it must NOT also pass `placeholder` — `Select` renders the placeholder
    // as a second `<option value="">`, which would show two "Unassigned" rows.
    const options =
      column.type === 'person'
        ? [{ value: '', label: 'Unassigned' }, ...baseOptions]
        : baseOptions;
    const strValue = value == null ? '' : String(value);
    return (
      <div onClick={stopRowClick}>
        <Select
          id={fieldId}
          label={column.label}
          hideLabel
          table
          data-testid={editTestId}
          value={strValue}
          placeholder={column.type === 'person' ? undefined : 'Select…'}
          options={options}
          onChange={(e) => {
            void onCommit(e.target.value);
          }}
        />
      </div>
    );
  }

  if (column.type === 'date') {
    const iso =
      value == null || value === '' || value === '—'
        ? null
        : String(value);
    return (
      <div onClick={stopRowClick}>
        <DateField
          id={fieldId}
          label={column.label}
          hideLabel
          table
          testId={editTestId}
          value={iso}
          onChange={(next) => {
            void onCommit(next ?? '');
          }}
        />
      </div>
    );
  }

  if (column.type === 'multi_select') {
    const options = column.options ?? [];
    const arr = Array.isArray(value) ? value : [];
    return (
      <div
        className="pn-table-cell-chips-wrap"
        data-testid={editTestId}
        onClick={stopRowClick}
      >
        <div className="pn-table-cell-chips">
          {options.map((o) => {
            const checked = arr.includes(o.value);
            return (
              <label key={o.value} className="pn-table-cell-chip">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? arr.filter((v) => v !== o.value)
                      : [...arr, o.value];
                    void onCommit(next);
                  }}
                />
                <span>{o.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div onClick={stopRowClick}>
        <TableCellEditor
          column={column}
          value={value}
          rowId={rowId}
          dataTestId={editTestId}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="pn-table-cell-text-btn pn-table-cell-truncate"
      title={text || undefined}
      data-testid={editTestId}
      onClick={(e) => {
        e.stopPropagation();
        onStartEdit();
      }}
    >
      <span className="pn-table-cell-text">{text || '—'}</span>
    </button>
  );
}

interface TableCellReadOnlyProps {
  column: Column;
  value: CellValue;
  text: string;
}

function TableCellReadOnly({ column, value, text }: TableCellReadOnlyProps) {
  if (column.key === 'type' && text) {
    return (
      <span className="pn-table-cell-readonly">
        <TypeChip>{text}</TypeChip>
      </span>
    );
  }

  if (
    (column.key === 'lifecycle' || column.key === 'status') &&
    text &&
    text !== '—'
  ) {
    return (
      <span className="pn-table-cell-readonly">
        <Pill status={pillStatusForCell(column.key, value)}>{text}</Pill>
      </span>
    );
  }

  if (column.type === 'person' && text && text !== '—') {
    return (
      <span
        className="pn-table-cell-readonly pn-table-cell-person"
        title={text}
      >
        <Avatar name={text} size="sm" />
        <span className="pn-table-cell-text" style={{ marginInlineStart: 'var(--space-2)' }}>
          {text}
        </span>
      </span>
    );
  }

  return (
    <span
      className="pn-table-cell-text pn-table-cell-truncate"
      title={text || undefined}
    >
      {text}
    </span>
  );
}
